import React, { useState, useEffect } from 'react';
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Sunrise, Sunset, Moon, CalendarDays,
} from 'lucide-react';
import { useShop } from '../../contexts/ShopContext';

const LOGO_URL = '/brand/twp-logo.png';
const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO    = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

export default function ShopSplashScreen({ user, onComplete }) {
  const { queueJobs, activeJobs } = useShop();
  const [weather, setWeather] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [visible, setVisible] = useState(true);

  const installerName = user?.displayName || user?.email?.split('@')[0] || 'Shop Worker';

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onComplete, 300);
    }, 3000);

    return () => {
      clearInterval(timeInterval);
      clearTimeout(timer);
    };
  }, [onComplete]);

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      const lat = 45.2197;
      const lon = -96.6295;
      
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&temperature_unit=fahrenheit&timezone=America/Chicago`
      );
      
      const data = await response.json();
      
      setWeather({
        temp: Math.round(data.current.temperature_2m),
        condition: getWeatherCondition(data.current.weather_code)
      });
    } catch (error) {
      console.error('Error fetching weather:', error);
    }
  };

  const getWeatherCondition = (code) => {
    const conditions = {
      0:  { label: 'Clear',            Icon: Sun },
      1:  { label: 'Mostly Clear',     Icon: CloudSun },
      2:  { label: 'Partly Cloudy',    Icon: CloudSun },
      3:  { label: 'Overcast',         Icon: Cloud },
      45: { label: 'Foggy',            Icon: CloudFog },
      48: { label: 'Foggy',            Icon: CloudFog },
      51: { label: 'Light Drizzle',    Icon: CloudDrizzle },
      53: { label: 'Drizzle',          Icon: CloudDrizzle },
      55: { label: 'Heavy Drizzle',    Icon: CloudRain },
      61: { label: 'Light Rain',       Icon: CloudRain },
      63: { label: 'Rain',             Icon: CloudRain },
      65: { label: 'Heavy Rain',       Icon: CloudRain },
      71: { label: 'Light Snow',       Icon: CloudSnow },
      73: { label: 'Snow',             Icon: CloudSnow },
      75: { label: 'Heavy Snow',       Icon: CloudSnow },
      77: { label: 'Snow Grains',      Icon: CloudSnow },
      80: { label: 'Rain Showers',     Icon: CloudDrizzle },
      81: { label: 'Rain Showers',     Icon: CloudRain },
      82: { label: 'Heavy Rain Showers', Icon: CloudLightning },
      85: { label: 'Snow Showers',     Icon: CloudSnow },
      86: { label: 'Heavy Snow Showers', Icon: CloudSnow },
      95: { label: 'Thunderstorm',     Icon: CloudLightning },
      96: { label: 'Thunderstorm',     Icon: CloudLightning },
      99: { label: 'Severe Thunderstorm', Icon: CloudLightning },
    };
    return conditions[code] || { label: 'Fair', Icon: CloudSun };
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) {
      return { text: `Good morning, ${installerName}!`, Icon: Sunrise };
    } else if (hour >= 12 && hour < 17) {
      return { text: `Good afternoon, ${installerName}!`, Icon: Sun };
    } else if (hour >= 17 && hour < 21) {
      return { text: `Good evening, ${installerName}!`, Icon: Sunset };
    } else {
      return { text: `Working late, ${installerName}?`, Icon: Moon };
    }
  };

  // Calculate completed this week from active jobs
  const completedThisWeek = activeJobs.filter(job => {
    if (job.productionStartedAt) {
      const startDate = job.productionStartedAt.toDate ? job.productionStartedAt.toDate() : new Date(job.productionStartedAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return startDate >= weekAgo;
    }
    return false;
  }).length;

  if (!visible) return null;

  const dateString = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const timeString = currentTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      animation: 'fadeOut 0.3s ease-out 2.7s forwards',
      padding: '20px',
      fontFamily: FONT_SANS
    }}>
      <style>{`
        @keyframes fadeOut {
          to { opacity: 0; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }
          50% {
            box-shadow: 0 25px 70px rgba(0, 0, 0, 0.4);
          }
        }
      `}</style>

      <div style={{
        textAlign: 'center',
        animation: 'slideUp 0.6s ease-out',
        maxWidth: '450px',
        width: '100%'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '40px',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'float 3s ease-in-out infinite, pulse 3s ease-in-out infinite',
          display: 'inline-block'
        }}>
          <img
            src={LOGO_URL}
            alt="The Window Place LLC"
            style={{
              maxWidth: '200px',
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
        </div>

        <h1 style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          fontFamily: FONT_DISPLAY,
          fontSize: '24px',
          fontWeight: '600',
          color: 'white',
          margin: '0 0 20px 0',
          textShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}>
          {(() => { const g = getGreeting(); return <><g.Icon size={22} strokeWidth={2} /> {g.text}</>; })()}
        </h1>

        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: FONT_SANS,
              fontSize: '13px',
              fontWeight: '600',
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '4px',
              textShadow: '0 1px 4px rgba(0,0,0,0.2)'
            }}>
              <CalendarDays size={13} strokeWidth={2.25} /> {dateString}
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'white',
              fontFamily: FONT_MONO,
              textShadow: '0 2px 6px rgba(0,0,0,0.3)',
              letterSpacing: '1px'
            }}>
              {timeString}
            </div>
          </div>

          {weather && (
            <div style={{
              textAlign: 'right',
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <div style={{
                fontFamily: FONT_SANS,
                fontSize: '11px',
                color: 'rgba(255,255,255,0.8)',
                marginBottom: '4px',
                textShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                Milbank, SD
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: 'white',
                textShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}>
                {weather.temp}°F
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px',
                fontFamily: FONT_SANS,
                fontSize: '12px',
                marginTop: '4px',
                color: 'rgba(255,255,255,0.9)',
                textShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                <weather.condition.Icon size={13} strokeWidth={2.25} /> {weather.condition.label}
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '16px 12px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: 'white',
              textShadow: '0 2px 6px rgba(0,0,0,0.3)'
            }}>
              {queueJobs.length}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: 'rgba(255,255,255,0.9)', 
              marginTop: '4px',
              textShadow: '0 1px 3px rgba(0,0,0,0.2)',
              fontWeight: '600'
            }}>
              In Queue
            </div>
          </div>

          <div style={{
            padding: '16px 12px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: 'white',
              textShadow: '0 2px 6px rgba(0,0,0,0.3)'
            }}>
              {activeJobs.length}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: 'rgba(255,255,255,0.9)', 
              marginTop: '4px',
              textShadow: '0 1px 3px rgba(0,0,0,0.2)',
              fontWeight: '600'
            }}>
              Active Now
            </div>
          </div>

          <div style={{
            padding: '16px 12px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: 'white',
              textShadow: '0 2px 6px rgba(0,0,0,0.3)'
            }}>
              {completedThisWeek}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: 'rgba(255,255,255,0.9)', 
              marginTop: '4px',
              textShadow: '0 1px 3px rgba(0,0,0,0.2)',
              fontWeight: '600'
            }}>
              This Week
            </div>
          </div>
        </div>

        <p style={{
          fontSize: '15px',
          color: 'rgba(255,255,255,0.95)',
          margin: '0 0 24px 0',
          fontWeight: '500',
          textShadow: '0 1px 4px rgba(0,0,0,0.2)',
          fontStyle: 'italic'
        }}>
          "Let's build something great today!"
        </p>

        <div style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.8)',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          fontWeight: '500'
        }}>
          © {new Date().getFullYear()} The Window Place LLC
        </div>
      </div>
    </div>
  );
}